import { KeyboardArrowLeft } from "@mui/icons-material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ReceiptIcon from "@mui/icons-material/Receipt";
import QrCodeIcon from "@mui/icons-material/QrCode";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Typography,
  Step,
  Stepper,
  StepLabel,
  Card
} from "@mui/material";
import React from "react";
import { Focused } from "react-credit-cards-2";
import { Link, NavigateFunction } from "react-router-dom";
import { AppliedCouponResponse, IInstrument, UpdateUserProfilePayload, UserProfile } from "../clients/answeringClient";
import { CartSingleton } from "../SharedComponents/CartSingleton";
import { Layout } from "../SharedComponents/Layout";
import { logout } from "../SharedComponents/LoginGuard/loginGuard";
import { withNavigationHooks } from "../SharedComponents/withNavigationHooks";
import { environment } from "../utils/environment";
import { getUserRolesFromToken } from "../utils/getUserRolesFromToken";
import { trackGtagEvent } from "../utils/analytics";
import {
  CEP,
  CheckoutPaymentController,
  CheckoutPaymentControllerDelegate,
  CheckoutPurchaseItem,
  IFullPaymentData,
  InstallmentsResponse,
  PaymentMethod,
  PaymentResponse,
  PaymentSummaryResponse,
  PaymentStatusResponse,
} from "./controller";
import { State as AddressState } from "./data-types";
import PaymentMethodForm from "./components/PaymentMethodForm";
import ReviewPanel from "./components/ReviewPanel";
import PaymentResult from "./components/PaymentResult";
import UserInfoForm, {
  AddressStateSnapshot,
  UserInfoFormHandle,
  UserProfileState,
} from "./components/UserInfoForm/UserInfoForm";
import { UserAddress } from "./components/UserInfoForm/Address/controller";

declare global {
  interface Window {
    yapay: {
      initialized: boolean;
      FingerPrint: (config: { env: string }) => {
        getFingerPrint: () => string;
      };
    };
  }
}

enum PaymentStep {
  USER_INFO,
  PAYMENT,
}

const EMPTY_ADDRESS = {
  cep: "",
  isProvidedByCep: {
    state: false,
    city: false,
    neighborhood: false,
    street: false,
  },
  state: AddressState.AC,
  city: "",
  neighborhood: "",
  street: "",
  number: "",
  completion: "",
};

const mapUserAddressToPaymentAddress = (address?: UserAddress) => {
  if (!address) {
    return { ...EMPTY_ADDRESS };
  }

  return {
    cep: address.postalCode,
    isProvidedByCep: {
      state: true,
      city: true,
      neighborhood: true,
      street: true,
    },
    state: address.state,
    city: address.city,
    neighborhood: address.neighborhood,
    street: address.street,
    number: address.number,
    completion: address.completion ?? "",
  };
};

interface State {
  error: {
    apiValidation: boolean;
    apiGeneral: boolean;
    user: {
      identification: boolean;
      birthDate: boolean;
      cellphone: boolean;
      cnpj: boolean;
    };
    address: {
      cep: boolean;
      state: boolean;
      city: boolean;
      neighborhood: boolean;
      street: boolean;
      number: boolean;
    };
    creditCard: {
      number: boolean;
      expiry: boolean;
      cvc: boolean;
      name: boolean;
    };
  };
  data: {
    currentStep: PaymentStep;
    focusedCCField?: Focused;
    paymentResponse?: PaymentResponse;
  } & IFullPaymentData;
  isLoading: boolean;
  installmentsLoading: boolean;
  installments?: InstallmentsResponse;
  installmentsError?: string;
  selectedInstallment: number;
  paymentSummaryLoading: boolean;
  paymentSummary?: PaymentSummaryResponse;
  paymentSummaryError?: string;
  purchaseErrorMessage?: string;
  paymentStatus?: PaymentStatusResponse;
  paymentStatusPolling: boolean;
  paymentStatusError?: string;
  selectedAddressId?: number;
  cartId?: number;
  addressesBusy: boolean;
  userProfileBusy: boolean;
  cartSummary: CartPricingSummary;
}

interface CartPricingSummary {
  subtotalCents: number;
  totalCents: number;
  couponDiscountCents: number;
  totalDiscountCents: number;
  progressiveDiscountCents: number;
  appliedCoupon: AppliedCouponResponse | null;
}

type PixelContent = {
  id: string;
  quantity: number;
  item_price: number;
};

type PurchasePixelPayload = {
  contentIds: string[];
  contents: PixelContent[];
  numItems: number;
  value: number;
  coupon: string | null;
  discountValue: number;
  paymentMethod: PaymentMethod;
  roles: string[];
};

interface Props {
  navigationHooks: NavigateFunction;
}

export class ManagedInstruments
  extends React.Component<Props, State>
  implements CheckoutPaymentControllerDelegate {

  private controller: CheckoutPaymentController;
  private scriptTag?: Node;
  private methods = [
    { id: PaymentMethod.CREDIT_CARD, label: "Cartão de Crédito", icon: <CreditCardIcon sx={{ color: "primary.main" }} /> },
    { id: PaymentMethod.PIX, label: "Pix", icon: <QrCodeIcon sx={{ color: "primary.main" }} /> },
    { id: PaymentMethod.BANK_SLIP, label: "Boleto bancário", icon: <ReceiptIcon sx={{ color: "primary.main" }} /> },
  ];
  private userInfoFormRef = React.createRef<UserInfoFormHandle>();
  private provideAccessToken = () => this.getAccessToken();
  private requestLogout = () => this.applicationShouldLogout();
  private paymentStatusInterval?: number;
  private paymentStatusAttempts = 0;
  private readonly paymentStatusIntervalMs = 15000;
  private readonly paymentStatusMaxAttempts = 60;
  private isUnmounted = false;
  private readonly cardBrandRequiredMessage =
    "Informe um cartão válido para consultar as parcelas disponíveis.";
  private readonly currencyFormatter = new Intl.NumberFormat("pt-BR", {
    currency: "BRL",
    style: "currency",
  });
  private cartListener = () => this.handleCartUpdated();
  private lastRequestedInstrumentIds: number[] = [];
  private lastTrackedPaymentMethod: PaymentMethod | null = null;
  private hasTrackedPurchase = false;
  private hasTrackedPaymentFailure = false;
  private pendingPurchasePayload: PurchasePixelPayload | null = null;

  private getSelectedPaymentMethodLabel() {
    const { selectedPaymentMethod, creditCard } = this.state.data;

    switch (selectedPaymentMethod) {
      case PaymentMethod.CREDIT_CARD:
        return creditCard.number.length === 16
          ? `Cartão de Crédito com final ${creditCard.number.slice(-4)}`
          : "Cartão de Crédito";
      case PaymentMethod.PIX:
        return "PIX";
      default:
        return "A Definir";
    }
  }

  constructor(props: Props) {
    super(props);
    this.controller = new CheckoutPaymentController(this);
    this.state = {
      error: {
        apiGeneral: false,
        apiValidation: false,
        user: {
          identification: false,
          birthDate: false,
          cellphone: false,
          cnpj: false,
        },
        address: {
          cep: false,
          state: false,
          city: false,
          neighborhood: false,
          street: false,
          number: false,
        },
        creditCard: {
          number: false,
          expiry: false,
          cvc: false,
          name: false,
        },
      },
      data: {
        currentStep: PaymentStep.USER_INFO,
        selectedPaymentMethod: null,
        purchaseData: [],
        user: {
          identification: "",
          identificationType: undefined,
          cnpj: undefined,
          birthDate: {
            day: "",
            month: "",
            year: "",
          },
          cellphone: "",
        },
        address: { ...EMPTY_ADDRESS },
        creditCard: {
          cvc: "",
          number: "",
          expiry: "",
          name: "",
          issuer: "",
          installments: 1,
        },
      },
      isLoading: true,
      installmentsLoading: false,
      installments: undefined,
      installmentsError: undefined,
      selectedInstallment: 1,
      paymentSummaryLoading: false,
      paymentSummary: undefined,
      paymentSummaryError: undefined,
      purchaseErrorMessage: undefined,
      paymentStatus: undefined,
      paymentStatusPolling: false,
      paymentStatusError: undefined,
      selectedAddressId: undefined,
      cartId: undefined,
      addressesBusy: true,
      userProfileBusy: false,
      cartSummary: this.buildEmptyCartSummary(),
    };
  }

  componentDidMount(): void {
    this.isUnmounted = false;
    const scriptTag = document.createElement("script");
    scriptTag.src = "https://static.traycheckout.com.br/js/finger_print.js";
    scriptTag.type = "text/javascript";
    scriptTag.async = true;

    document.body.appendChild(scriptTag);
    this.scriptTag = scriptTag;
    CartSingleton.shared.addCartUpdatedListener(this.cartListener);
    void this.loadData();
  }

  componentWillUnmount(): void {
    this.isUnmounted = true;
    this.stopPaymentStatusPolling(true);
    CartSingleton.shared.removeCartUpdatedListener(this.cartListener);
    if (this.scriptTag && this.scriptTag.parentNode) {
      this.scriptTag.parentNode.removeChild(this.scriptTag);
    }
  }

  // Delegate
  didCreatePurchase(data: PaymentResponse): void {
    this.stopPaymentStatusPolling();
    void CartSingleton.shared.clear();
    const initialStatus = this.derivePaymentStatusFromResponse(data);
    this.setState(
      {
        isLoading: false,
        data: {
          ...this.state.data,
          paymentResponse: data,
        },
        purchaseErrorMessage: undefined,
        paymentStatus: initialStatus,
        paymentStatusError: undefined,
        paymentStatusPolling: false,
      },
      () => {
        const hasOrderNumber = Boolean(data.orderNumber);
        const selectedMethod = this.state.data.selectedPaymentMethod;
        const shouldPoll =
          hasOrderNumber &&
          (selectedMethod === PaymentMethod.PIX ||
            selectedMethod === PaymentMethod.CREDIT_CARD) &&
          (!initialStatus || !this.isTerminalPaymentStatus(initialStatus));
        if (shouldPoll && data.orderNumber) {
          this.startPaymentStatusPolling(data.orderNumber);
        }
        this.maybeTrackPurchase();
        this.maybeTrackPaymentFailureFromStatus(initialStatus ?? null);
      }
    );
  }

  errorCreatingPurchase(options: { isValidation: boolean; message?: string }): void {
    this.stopPaymentStatusPolling();
    this.pendingPurchasePayload = null;
    this.hasTrackedPurchase = false;
    this.trackPaymentFailed(options.message, this.state.data.paymentResponse?.orderNumber);
    const purchaseErrorMessage = this.formatPurchaseErrorMessage(options.message);
    this.setState({
      isLoading: false,
      error: {
        ...this.state.error,
        apiValidation: options.isValidation,
        apiGeneral: !options.isValidation,
      },
      purchaseErrorMessage,
      paymentStatus: undefined,
      paymentStatusError: undefined,
      paymentStatusPolling: false,
    });
  }

  didGetCepData(data: CEP): void {
    this.setState({
      data: {
        ...this.state.data,
        address: {
          ...this.state.data.address,
          state: data.state,
          street: data.street,
          city: data.city,
          neighborhood: data.neighborhood,
          isProvidedByCep: {
            state: !!data.state,
            city: !!data.city,
            neighborhood: !!data.neighborhood,
            street: !!data.street,
          },
        },
      },
    });
  }

  cepDataError(): void {
    this.setState({
      data: {
        ...this.state.data,
        address: {
          ...this.state.data.address,
          isProvidedByCep: {
            state: false,
            city: false,
            neighborhood: false,
            street: false,
          },
        },
      },
    });
  }

  didGetInstruments(instruments: IInstrument[]): void {
    this.setState(
      (prev) => {
        const purchaseData = prev.data.purchaseData.map((item) => {
          const selectedInstrument = instruments.find(
            (instrument) => instrument.id === item.id
          );

          if (!selectedInstrument) {
            return item;
          }

          return {
            ...item,
            description: selectedInstrument.description,
          };
        });

        return {
          isLoading: false,
          data: {
            ...prev.data,
            purchaseData,
          },
        };
      },
      () => {
        if (!this.isUnmounted) {
          this.refreshPaymentData();
        }
      }
    );
  }

  applicationShouldLogout(): void {
    logout();
  }

  genericError(message?: string): void {
    this.setState((prev) => ({
      isLoading: false,
      error: {
        ...prev.error,
        apiGeneral: true,
        apiValidation: false,
      },
      purchaseErrorMessage:
        message ?? "Não foi possível carregar as informações necessárias. Tente novamente.",
    }));
  }

  // Internals
  private formatPurchaseErrorMessage(message?: string) {
    if (!message) {
      return message;
    }

    const normalized = message.trim();
    if (normalized.includes('"paymentInfo.fingerprint" is not allowed to be empty')) {
      return "Não conseguimos validar seu dispositivo. Desative temporariamente bloqueadores de anúncios ou antivírus e tente novamente.";
    }

    return message;
  }

  private getAccessToken(): string | null {
    const token = localStorage.getItem("AccessToken");
    if (!token) {
      this.applicationShouldLogout();
      return null;
    }
    return token;
  }

  private getCartTotalInCents(): number {
    if (this.state.cartSummary?.totalCents != null) {
      return this.state.cartSummary.totalCents;
    }

    return this.state.data.purchaseData.reduce((acc, cur) => {
      return acc + cur.priceInBrlCents * cur.quantity;
    }, 0);
  }

  private trackAddPaymentInfo(method: PaymentMethod | null | undefined) {
    if (
      !method ||
      typeof window === "undefined" ||
      typeof document === "undefined" ||
      !document.cookie.includes("catvanteAnalyticsCookiesAccepted=true")
    ) {
      return;
    }

    if (this.lastTrackedPaymentMethod === method) {
      return;
    }

    const purchaseData = this.state.data.purchaseData ?? [];
    if (purchaseData.length === 0) {
      return;
    }

    const contents = purchaseData.map((item) => {
      const unitCents = item.discountedUnitPriceCents ?? item.unitPriceCents;
      return {
        id: String(item.id),
        quantity: item.quantity,
        item_price: unitCents / 100,
      };
    });

    if (contents.length === 0) {
      return;
    }

    const contentIds = contents.map((item) => item.id);
    const totalCents = this.getCartTotalInCents();
    const roles = getUserRolesFromToken();

    const fbq = window.fbq;
    if (fbq) {
      fbq("track", "AddPaymentInfo", {
        content_ids: contentIds,
        contents,
        currency: "BRL",
        value: totalCents / 100,
        payment_method: method,
        roles,
      });
    }

    const gaItems = contents.map((content) => ({
      item_id: content.id,
      quantity: content.quantity,
      price: content.item_price,
    }));
    const paymentType = this.mapPaymentMethodForPixel(method);
    trackGtagEvent("add_payment_info", {
      currency: "BRL",
      value: totalCents / 100,
      payment_type: paymentType,
      items: gaItems,
      roles,
    });

    this.lastTrackedPaymentMethod = method;
  }

  private preparePurchasePixelPayload(
    method: PaymentMethod,
    fallbackAmountCents: number
  ) {
    const purchaseItems = this.state.data.purchaseData ?? [];
    if (purchaseItems.length === 0) {
      this.pendingPurchasePayload = null;
      return;
    }

    const contents: PixelContent[] = purchaseItems.map((item) => {
      const unitCents =
        item.discountedUnitPriceCents ??
        item.unitPriceCents ??
        item.priceInBrlCents;
      const normalizedUnitCents =
        typeof unitCents === "number" ? unitCents : 0;
      return {
        id: String(item.id),
        quantity: item.quantity,
        item_price: Number((normalizedUnitCents / 100).toFixed(2)),
      };
    });

    const contentIds = contents.map((content) => content.id);
    const numItems = purchaseItems.reduce(
      (total, item) => total + item.quantity,
      0
    );

    let valueCents = fallbackAmountCents;
    const summary = this.state.paymentSummary;
    if (summary && summary.method === method) {
      if (typeof summary.estimated_final_amount_cents === "number") {
        valueCents = summary.estimated_final_amount_cents;
      } else if (typeof summary.amount_cents === "number") {
        valueCents = summary.amount_cents;
      }
    }

    const coupon = this.state.cartSummary.appliedCoupon?.code ?? null;
    const discountValueCents = this.state.cartSummary.totalDiscountCents ?? 0;
    const roles = getUserRolesFromToken();

    this.pendingPurchasePayload = {
      contentIds,
      contents,
      numItems,
      value: Number((valueCents / 100).toFixed(2)),
      coupon,
      discountValue: Number((discountValueCents / 100).toFixed(2)),
      paymentMethod: method,
      roles,
    };
    this.hasTrackedPurchase = false;
    this.hasTrackedPaymentFailure = false;
  }

  private mapPaymentMethodForPixel(method: PaymentMethod): string {
    switch (method) {
      case PaymentMethod.CREDIT_CARD:
        return "credit_card";
      case PaymentMethod.PIX:
        return "pix";
      case PaymentMethod.BANK_SLIP:
        return "bank_slip";
    }
  }

  private isApprovedStatus(statusId?: number | null): boolean {
    return statusId === 6;
  }

  private maybeTrackPurchase() {
    if (
      this.hasTrackedPurchase ||
      !this.pendingPurchasePayload ||
      typeof window === "undefined" ||
      typeof document === "undefined" ||
      !document.cookie.includes("catvanteAnalyticsCookiesAccepted=true")
    ) {
      return;
    }

    const currentStatus = this.state.paymentStatus;
    const orderId =
      currentStatus?.orderNumber ??
      this.state.data.paymentResponse?.orderNumber;

    if (!orderId) {
      return;
    }

    const payload = this.pendingPurchasePayload;

    const fbq = window.fbq;
    if (fbq) {
      fbq("track", "Purchase", {
        order_id: String(orderId),
        content_ids: payload.contentIds,
        contents: payload.contents,
        num_items: payload.numItems,
        value: payload.value,
        currency: "BRL",
        coupon: payload.coupon ?? "",
        discount_value: payload.discountValue,
        payment_method: this.mapPaymentMethodForPixel(payload.paymentMethod),
        roles: payload.roles,
      });
    }

    const gaItems = payload.contents.map((content) => ({
      item_id: content.id,
      quantity: content.quantity,
      price: content.item_price,
    }));
    trackGtagEvent("purchase", {
      transaction_id: String(orderId),
      currency: "BRL",
      value: payload.value,
      coupon: payload.coupon ?? undefined,
      discount_value: payload.discountValue,
      payment_type: this.mapPaymentMethodForPixel(payload.paymentMethod),
      items: gaItems,
      items_count: payload.numItems,
      roles: payload.roles,
    });

    this.hasTrackedPurchase = true;
    this.pendingPurchasePayload = null;
  }

  private isFailureStatus(
    statusId?: number | null,
    expired?: boolean
  ): boolean {
    if (expired) {
      return true;
    }
    const failureStatuses = new Set([7, 24, 87, 89]);
    return statusId != null && failureStatuses.has(statusId);
  }

  private trackPaymentFailed(reason?: string, orderId?: string | number | null) {
    if (
      this.hasTrackedPaymentFailure ||
      typeof window === "undefined" ||
      typeof document === "undefined" ||
      !document.cookie.includes("catvanteAnalyticsCookiesAccepted=true")
    ) {
      return;
    }

    const fbq = window.fbq;
    if (fbq) {
      fbq("trackCustom", "PaymentFailed", {
        reason: reason ?? "unknown",
        order_id: orderId ? String(orderId) : "",
      });
    }

    trackGtagEvent("payment_failed", {
      reason: reason ?? "unknown",
      order_id: orderId ? String(orderId) : "",
    });

    this.hasTrackedPaymentFailure = true;
  }

  private maybeTrackPaymentFailureFromStatus(
    status?: PaymentStatusResponse | null
  ) {
    if (!status || this.hasTrackedPaymentFailure) {
      return;
    }

    if (this.isApprovedStatus(status.paymentStatusId)) {
      return;
    }

    if (!this.isFailureStatus(status.paymentStatusId, status.expired)) {
      return;
    }

    const reason =
      status.paymentStatusName ??
      this.getPaymentStatusFallback(status.paymentStatusId ?? 0);
    this.trackPaymentFailed(reason, status.orderNumber);
  }

  private buildEmptyCartSummary(): CartPricingSummary {
    return {
      subtotalCents: 0,
      totalCents: 0,
      couponDiscountCents: 0,
      totalDiscountCents: 0,
      progressiveDiscountCents: 0,
      appliedCoupon: null,
    };
  }

  private formatCurrency(amountCents: number): string {
    return this.currencyFormatter.format(amountCents / 100);
  }

  private resetInstallmentsState() {
    this.setState((prev) => ({
      installments: undefined,
      installmentsError: undefined,
      installmentsLoading: false,
      selectedInstallment: 1,
      data: {
        ...prev.data,
        creditCard: {
          ...prev.data.creditCard,
          installments: 1,
        },
      },
    }));
  }

  private getPaymentMethodLabel(method: PaymentMethod | null | undefined): string {
    if (!method) return "Método desconhecido";
    const entry = this.methods.find((m) => m.id === method);
    return entry?.label ?? method.replace(/_/g, " ");
  }

  private getPaymentStatusFallback(statusId: number): string {
    switch (statusId) {
      case 4:
        return "Aguardando Pagamento";
      case 6:
        return "Pagamento Confirmado";
      case 7:
        return "Pagamento Cancelado";
      default:
        return "Status atualizado";
    }
  }

  private derivePaymentStatusFromResponse(
    response: PaymentResponse
  ): PaymentStatusResponse | undefined {
    if (!response.orderNumber) return undefined;

    const paymentStatusId =
      response.paymentStatusId != null ? response.paymentStatusId : 4;
    const paymentMethodId =
      response.paymentMethodId != null ? response.paymentMethodId : 0;
    const paymentMethodName =
      response.paymentMethodName ??
      this.getPaymentMethodLabel(this.state.data.selectedPaymentMethod);

    return {
      orderNumber: response.orderNumber,
      paymentStatusId,
      paymentStatusName:
        response.paymentStatusName ??
        this.getPaymentStatusFallback(paymentStatusId),
      paymentMethodId,
      paymentMethodName,
      installments:
        response.installments ??
        this.state.data.creditCard.installments ??
        1,
      paymentUrl: response.paymentUrl,
      pixQrcodePath: response.paymentQrCode,
      pixQrcodeOriginalPath: response.paymentPixCopyPaste,
      paymentDueAt: response.dueDate ? response.dueDate.toISOString() : undefined,
      expired: response.expired ?? false,
    };
  }

  private mergePaymentStatusIntoResponse(
    response: PaymentResponse | undefined,
    status: PaymentStatusResponse
  ): PaymentResponse {
    const next: PaymentResponse = {
      ...(response ?? {}),
      orderNumber: status.orderNumber,
      paymentStatusId: status.paymentStatusId,
      paymentStatusName: status.paymentStatusName,
      paymentMethodId: status.paymentMethodId,
      paymentMethodName: status.paymentMethodName,
      installments: status.installments,
      expired: status.expired,
    };

    if (status.paymentUrl) {
      next.paymentUrl = status.paymentUrl;
    }
    if (status.pixQrcodePath) {
      next.paymentQrCode = status.pixQrcodePath;
    }
    if (status.pixQrcodeOriginalPath) {
      next.paymentPixCopyPaste = status.pixQrcodeOriginalPath;
    }
    if (status.paymentDueAt) {
      next.dueDate = new Date(status.paymentDueAt);
    }

    return next;
  }

  private updatePaymentStatus(status: PaymentStatusResponse) {
    if (this.isUnmounted) return;
    this.setState(
      (prev) => ({
        paymentStatus: status,
        paymentStatusError: undefined,
        data: {
          ...prev.data,
          paymentResponse: this.mergePaymentStatusIntoResponse(
            prev.data.paymentResponse,
            status
          ),
        },
      }),
      () => {
        this.maybeTrackPurchase();
        this.maybeTrackPaymentFailureFromStatus(status);
      }
    );
  }

  private isTerminalPaymentStatus(status: PaymentStatusResponse): boolean {
    const terminalIds = new Set([6, 7, 24, 87, 89]);
    return status.expired || terminalIds.has(status.paymentStatusId);
  }

  private async pollAndHandlePaymentStatus(
    orderNumber: string
  ): Promise<boolean> {
    const token = this.getAccessToken();
    if (!token) {
      return true;
    }

    try {
      const status = await this.controller.getPaymentStatus(token, orderNumber);
      this.updatePaymentStatus(status);
      return this.isTerminalPaymentStatus(status);
    } catch (error: any) {
      const message = this.extractApiError(
        error,
        "Não foi possível consultar o status do pagamento."
      );
      if (!this.isUnmounted) {
        this.setState({ paymentStatusError: message });
      }
      return true;
    }
  }

  private startPaymentStatusPolling(orderNumber: string) {
    if (!orderNumber) return;

    this.stopPaymentStatusPolling();
    this.paymentStatusAttempts = 0;
    if (!this.isUnmounted) {
      this.setState({
        paymentStatusPolling: true,
        paymentStatusError: undefined,
      });
    }

    const executePoll = async (): Promise<boolean> => {
      this.paymentStatusAttempts += 1;
      const shouldStop = await this.pollAndHandlePaymentStatus(orderNumber);
      if (shouldStop) {
        return false;
      }
      if (this.paymentStatusAttempts >= this.paymentStatusMaxAttempts) {
        if (!this.isUnmounted) {
          this.setState((prev) => ({
            paymentStatusError:
              prev.paymentStatusError ??
              "Não foi possível confirmar o status do pagamento. Continue acompanhando pelo extrato do provedor.",
          }));
        }
        return false;
      }
      return true;
    };

    void executePoll().then((shouldContinue) => {
      if (!shouldContinue) {
        this.stopPaymentStatusPolling();
        return;
      }

      this.paymentStatusInterval = window.setInterval(() => {
        void executePoll().then((continuePolling) => {
          if (!continuePolling) {
            this.stopPaymentStatusPolling();
          }
        });
      }, this.paymentStatusIntervalMs);
    });
  }

  private stopPaymentStatusPolling(skipStateUpdate = false) {
    if (this.paymentStatusInterval !== undefined) {
      window.clearInterval(this.paymentStatusInterval);
      this.paymentStatusInterval = undefined;
    }
    this.paymentStatusAttempts = 0;
    if (!skipStateUpdate && !this.isUnmounted) {
      this.setState({ paymentStatusPolling: false });
    }
  }

  private extractApiError(error: any, fallback: string): string {
    const data = error?.response?.data;

    if (typeof data === "string") {
      return data;
    }

    if (data?.error === "BRAND_NOT_ALLOWED") {
      const allowed = Array.isArray(data?.allowed)
        ? data.allowed.join(", ")
        : undefined;
      return allowed
        ? `Bandeira não permitida. Permitidas: ${allowed}.`
        : "Bandeira não permitida para pagamentos.";
    }

    if (data?.error === "AMOUNT_BELOW_MIN_FOR_CARD") {
      const min = data?.details?.min_installment_value_cents;
      return typeof min === "number"
        ? `Valor abaixo do mínimo por parcela (${this.formatCurrency(min)}).`
        : "Valor abaixo do mínimo permitido para parcelamento.";
    }

    if (data?.error === "INVALID_INSTALLMENTS") {
      const reason = data?.details?.reason;
      if (reason === "ABOVE_ALLOWED_FOR_AMOUNT") {
        const max = data?.details?.max_allowed_installments_for_amount;
        return typeof max === "number"
          ? `Número de parcelas indisponível para este valor (máximo ${max}).`
          : "Número de parcelas inválido para este valor.";
      }
      if (reason === "ABOVE_POLICY_MAX_TOTAL") {
        const maxPolicy = data?.details?.policy_max_total;
        return typeof maxPolicy === "number"
          ? `Número de parcelas acima do limite (${maxPolicy}).`
          : "Número de parcelas acima do limite permitido.";
      }
    }

    if (data?.message) {
      return data.message;
    }

    if (data?.error && typeof data.error === "string") {
      return data.error;
    }

    if (error?.message) {
      return error.message;
    }

    return fallback;
  }

  private async loadCardInstallments(brand: string) {
    const token = this.getAccessToken();
    if (!token) {
      return;
    }

    const amountCents = this.getCartTotalInCents();
    if (amountCents <= 0) {
      this.setState({
        installments: undefined,
        installmentsError: "Carrinho sem itens para parcelamento.",
        installmentsLoading: false,
      });
      return;
    }

    const normalizedBrand = brand.toUpperCase();

    this.setState({
      installmentsLoading: true,
      installmentsError: undefined,
    });

    try {
      const response = await this.controller.getInstallmentOptions(token, {
        amountCents,
        brand: normalizedBrand,
        simulate: true,
      });

      const defaultInstallment =
        response.options.length > 0 ? response.options[0].n : 1;

      this.setState(
        (prev) => {
          const hasCurrent = response.options.some(
            (option) =>
              option.n === prev.selectedInstallment &&
              option.n <= response.max_allowed_installments_for_amount
          );

          const nextInstallment = hasCurrent
            ? prev.selectedInstallment
            : defaultInstallment;

          return {
            installments: response,
            installmentsLoading: false,
            installmentsError: undefined,
            selectedInstallment: nextInstallment || 1,
            data: {
              ...prev.data,
              creditCard: {
                ...prev.data.creditCard,
                installments: nextInstallment || 1,
              },
            },
          };
        },
        () => {
          const selectedInstallment =
            this.state.data.creditCard.installments ?? this.state.selectedInstallment;
          this.loadPaymentSummaryInternal(
            PaymentMethod.CREDIT_CARD,
            normalizedBrand,
            selectedInstallment
          );
        }
      );
    } catch (error: any) {
      const message = this.extractApiError(
        error,
        "Não foi possível carregar as opções de parcelamento."
      );

      this.setState({
        installmentsLoading: false,
        installmentsError: message,
        installments: undefined,
      });

      this.loadPaymentSummaryInternal(PaymentMethod.CREDIT_CARD, normalizedBrand);
    }
  }

  private async loadPaymentSummaryInternal(
    method: PaymentMethod,
    brand?: string,
    installments?: number
  ) {
    const token = this.getAccessToken();
    if (!token) {
      return;
    }

    const amountCents = this.getCartTotalInCents();
    if (amountCents <= 0) {
      this.setState({
        paymentSummary: undefined,
        paymentSummaryError: "Carrinho sem itens.",
        paymentSummaryLoading: false,
      });
      return;
    }

    if (method === PaymentMethod.CREDIT_CARD && !brand) {
      this.setState({
        paymentSummary: undefined,
        paymentSummaryError: this.cardBrandRequiredMessage,
        paymentSummaryLoading: false,
      });
      return;
    }

    this.setState({
      paymentSummaryLoading: true,
      paymentSummaryError: undefined,
    });

    try {
      const response = await this.controller.getPaymentSummary(token, {
        method,
        amountCents,
        brand,
        installments,
      });

      this.setState({
        paymentSummary: response,
        paymentSummaryLoading: false,
        paymentSummaryError: undefined,
      });
    } catch (error: any) {
      const message = this.extractApiError(
        error,
        "Não foi possível carregar o resumo do pagamento."
      );

      this.setState({
        paymentSummary: undefined,
        paymentSummaryLoading: false,
        paymentSummaryError: message,
      });
    }
  }

  private refreshPaymentData() {
    const method = this.state.data.selectedPaymentMethod;
    if (!method) {
      return;
    }

    if (method === PaymentMethod.CREDIT_CARD) {
      const issuer = this.state.data.creditCard.issuer?.toUpperCase();
      if (issuer && issuer !== "UNKNOWN") {
        this.loadCardInstallments(issuer);
      } else {
        this.resetInstallmentsState();
        this.loadPaymentSummaryInternal(PaymentMethod.CREDIT_CARD);
      }
      return;
    }

    this.loadPaymentSummaryInternal(method);
  }

  private fetchUserProfile = async (): Promise<UserProfile | undefined> => {
    const token = this.getAccessToken();
    if (!token) {
      return undefined;
    }
    return this.controller.getUserProfile(token);
  }

  private persistUserProfile = async (
    payload: UpdateUserProfilePayload
  ): Promise<UserProfile | undefined> => {
    const token = this.getAccessToken();
    if (!token) {
      return undefined;
    }
    return this.controller.updateUserProfile(token, payload);
  }

  async loadData() {
    this.setState({
      isLoading: true,
      purchaseErrorMessage: undefined,
    });

    try {
      await CartSingleton.shared.refreshCart();
    } catch (error) {
      this.genericError(
        "Não foi possível carregar o carrinho. Atualize a página ou tente novamente."
      );
    }
  }

  private handleCartUpdated() {
    if (this.isUnmounted) {
      return;
    }

    const snapshot = CartSingleton.shared.getCartSnapshot();

    if (!snapshot) {
      this.lastRequestedInstrumentIds = [];
      this.lastTrackedPaymentMethod = null;
      this.hasTrackedPaymentFailure = false;
      this.setState((prev) => ({
        isLoading: false,
        purchaseErrorMessage: prev.purchaseErrorMessage,
        paymentSummary: undefined,
        paymentSummaryError: undefined,
        paymentSummaryLoading: false,
        installments: undefined,
        installmentsError: undefined,
        installmentsLoading: false,
        data: {
          ...prev.data,
          purchaseData: [],
          selectedPaymentMethod: null,
        },
        cartId: undefined,
        cartSummary: this.buildEmptyCartSummary(),
      }));
      return;
    }

    const previousById = new Map(
      this.state.data.purchaseData.map((item) => [item.id, item])
    );

    const purchaseData = snapshot.items.map((item) => {
      const previous = previousById.get(item.instrumentId);
      const purchaseItem: CheckoutPurchaseItem = {
        id: item.instrumentId,
        title: item.title,
        description: previous?.description,
        priceInBrlCents: item.discountedUnitPriceCents,
        quantity: item.quantity,
        priceChanged: item.priceChanged,
        priceSnapshotCents: item.priceSnapshotCents,
        unitPriceCents: item.unitPriceCents,
        discountedUnitPriceCents: item.discountedUnitPriceCents,
        subtotalCents: item.subtotalCents,
        discountedSubtotalCents: item.discountedSubtotalCents,
        totalDiscountCents: item.totalDiscountCents,
        appliedDiscountPct: item.appliedDiscountPct,
      };

      return purchaseItem;
    });

    const instrumentIds = snapshot.items
      .map((item) => item.instrumentId)
      .sort((a, b) => a - b);

    const isEmpty = purchaseData.length === 0;
    if (isEmpty) {
      this.lastTrackedPaymentMethod = null;
    }

    this.setState(
      (prev) => ({
        isLoading: false,
        purchaseErrorMessage: isEmpty
          ? prev.purchaseErrorMessage
          : undefined,
        error: {
          ...prev.error,
          apiGeneral: false,
          apiValidation: isEmpty ? prev.error.apiValidation : false,
        },
        paymentSummary: isEmpty ? undefined : prev.paymentSummary,
        paymentSummaryError: isEmpty ? undefined : prev.paymentSummaryError,
        paymentSummaryLoading: isEmpty ? false : prev.paymentSummaryLoading,
        installments: isEmpty ? undefined : prev.installments,
        installmentsError: isEmpty ? undefined : prev.installmentsError,
        installmentsLoading: isEmpty ? false : prev.installmentsLoading,
        data: {
          ...prev.data,
          selectedPaymentMethod: isEmpty ? null : prev.data.selectedPaymentMethod,
          purchaseData,
        },
        cartId: snapshot.id,
        cartSummary: {
          subtotalCents: snapshot.subtotalCents ?? 0,
          totalCents: snapshot.totalCents ?? snapshot.subtotalCents ?? 0,
          couponDiscountCents: snapshot.couponDiscountCents ?? 0,
          totalDiscountCents: snapshot.totalDiscountCents ?? 0,
          progressiveDiscountCents: snapshot.progressiveDiscountCents ?? 0,
          appliedCoupon: snapshot.appliedCoupon
            ? { ...snapshot.appliedCoupon }
            : null,
        },
      }),
      () => {
        if (!isEmpty) {
          this.pendingPurchasePayload = null;
          this.hasTrackedPurchase = false;
          this.hasTrackedPaymentFailure = false;
        }

        if (isEmpty) {
          this.hasTrackedPaymentFailure = false;
          this.resetInstallmentsState();
          return;
        }

        if (!this.isUnmounted) {
          this.refreshPaymentData();
        }
      }
    );

    if (snapshot.items.length === 0) {
      this.lastRequestedInstrumentIds = [];
      return;
    }

    if (
      instrumentIds.length > 0 &&
      !this.areInstrumentListsEqual(
        this.lastRequestedInstrumentIds,
        instrumentIds
      )
    ) {
      this.lastRequestedInstrumentIds = [...instrumentIds];
      const token = this.getAccessToken();
      if (!token) {
        return;
      }
      this.controller.getManagedInstruments(token, instrumentIds);
    }
  }

  private areInstrumentListsEqual(a: number[], b: number[]): boolean {
    if (a.length !== b.length) {
      return false;
    }
    return a.every((value, index) => value === b[index]);
  }

  private handleUserChange = (user: State["data"]["user"], errors: State["error"]["user"]) => {
    this.setState((prev) => ({
      data: {
        ...prev.data,
        user,
      },
      error: {
        ...prev.error,
        user: errors,
      },
    }));
  };

  private handleProfileStateChange = (state: UserProfileState) => {
    this.setState({ userProfileBusy: state.loading || state.saving });
  };

  didClickStep(step: PaymentStep) {
    this.setState({
      data: {
        ...this.state.data,
        currentStep: step,
      },
      purchaseErrorMessage: undefined,
    }, () => {
      if (step === PaymentStep.PAYMENT) {
        this.refreshPaymentData();
      }
    });
  }

  private async handleProceedToPayment() {
    if (this.state.data.purchaseData.length === 0) {
      this.setState((prev) => ({
        error: {
          ...prev.error,
          apiGeneral: false,
          apiValidation: true,
        },
        purchaseErrorMessage:
          "Adicione itens ao carrinho antes de avançar para o pagamento.",
      }));
      return;
    }

    const profilePersisted =
      (await this.userInfoFormRef.current?.ensureProfilePersisted?.()) ?? true;
    if (!profilePersisted) {
      this.setState({ isLoading: false });
      return;
    }

    this.didClickStep(PaymentStep.PAYMENT);
  }

  didChangePaymentMethod(event: any) {
    const selectedMethod = event.target.value as PaymentMethod;

    this.setState(
      (prev) => ({
        data: {
          ...prev.data,
          selectedPaymentMethod: selectedMethod,
        },
        error: {
          ...prev.error,
          creditCard: {
            ...prev.error.creditCard,
            number:
              selectedMethod === PaymentMethod.CREDIT_CARD
                ? prev.data.creditCard.number.length !== 16
                : false,
          },
        },
        paymentSummary: undefined,
        paymentSummaryError: undefined,
      }),
      () => {
        if (selectedMethod === PaymentMethod.CREDIT_CARD) {
          const issuer = this.state.data.creditCard.issuer?.toUpperCase();
          if (issuer && issuer !== "UNKNOWN") {
            this.loadCardInstallments(issuer);
          } else {
            this.resetInstallmentsState();
            this.loadPaymentSummaryInternal(PaymentMethod.CREDIT_CARD);
          }
        } else if (selectedMethod) {
          this.resetInstallmentsState();
          this.loadPaymentSummaryInternal(selectedMethod);
        }
      }
    );
  }

  private handleAddressChange = (address?: UserAddress) => {
    const nextAddressData = mapUserAddressToPaymentAddress(address);
    this.setState((prev) => ({
      selectedAddressId: address?.id,
      data: {
        ...prev.data,
        address: nextAddressData,
      },
    }));
  };

  private handleAddressStateChange = (snapshot: AddressStateSnapshot) => {
    this.setState((prev) => {
      const nextState: State = {
        ...prev,
        addressesBusy: snapshot.loading,
      };

      if (snapshot.selectedAddressId !== undefined) {
        nextState.selectedAddressId = snapshot.selectedAddressId;
      }

      if (!snapshot.hasAddresses && !snapshot.loading) {
        nextState.selectedAddressId = undefined;
        nextState.data = {
          ...prev.data,
          address: { ...EMPTY_ADDRESS },
        };
      }

      return nextState;
    });
  };

  // ** PAY METHOD **
  handlePaymentMethodChange = (event: any) => {
    this.didChangePaymentMethod(event);
  };

  // ** CREDIT CARD **
  didChangeCCFocus(field: Focused | undefined) {
    this.setState({
      data: {
        ...this.state.data,
        focusedCCField: field,
      },
    });
  }

  handleCCNumberChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const input = event.target.value;
    const digitsOnly = input.replace(/\D/g, "").slice(0, 16); // máximo 16 dígitos

    this.setState((prev) => ({
      data: {
        ...prev.data,
        creditCard: {
          ...prev.data.creditCard,
          number: digitsOnly,
        },
      },
      error: {
        ...prev.error,
        creditCard: {
          ...prev.error.creditCard,
          number: digitsOnly.length < 13, // ex: precisa ter 13-16 dígitos
        },
      },
    }));
  };

  handleCCNameChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const input = event.target.value;

    this.setState((prev) => ({
      data: {
        ...prev.data,
        creditCard: {
          ...prev.data.creditCard,
          name: input,
        },
      },
      error: {
        ...prev.error,
        creditCard: {
          ...prev.error.creditCard,
          name: input.trim().length < 5, // mínimo 5 chars
        },
      },
    }));
  };

  handleCCCVCChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const input = event.target.value;
    const digitsOnly = input.replace(/\D/g, "").slice(0, 4); // 3 ou 4 dígitos

    this.setState((prev) => ({
      data: {
        ...prev.data,
        creditCard: {
          ...prev.data.creditCard,
          cvc: digitsOnly,
        },
      },
      error: {
        ...prev.error,
        creditCard: {
          ...prev.error.creditCard,
          cvc: digitsOnly.length < 3, // pelo menos 3 dígitos
        },
      },
    }));
  };

  handleCCExpiryChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const input = event.target.value;
    const digitsOnly = input.replace(/\D/g, "").slice(0, 4); // MMYY

    this.setState((prev) => ({
      data: {
        ...prev.data,
        creditCard: {
          ...prev.data.creditCard,
          expiry: digitsOnly,
        },
      },
      error: {
        ...prev.error,
        creditCard: {
          ...prev.error.creditCard,
          expiry: digitsOnly.length !== 4,
        },
      },
    }));
  };

  didIdentifyCreditCard(data: { issuer: string }, isValid: boolean) {
    const detected = data?.issuer ? data.issuer.toUpperCase() : "";
    // Map the credit-card-type token to the backend brand enum; any token not in
    // the accepted set collapses to "UNKNOWN" (handled by the installments /
    // summary / purchase guards). Only Amex's token differs from the enum
    // ("american-express" -> "AMEX"); the old react-credit-cards `payment` lib
    // already emitted "amex". This allow-list also stops a non-enum brand from
    // reaching the money call if the acceptedCards display filter ever changes.
    const BRAND_BY_TOKEN: Record<string, string> = {
      VISA: "VISA",
      MASTERCARD: "MASTERCARD",
      "AMERICAN-EXPRESS": "AMEX",
      ELO: "ELO",
      HIPERCARD: "HIPERCARD",
      HIPER: "HIPER",
    };
    const issuer = BRAND_BY_TOKEN[detected] ?? "UNKNOWN";

    this.setState(
      (prev) => ({
        data: {
          ...prev.data,
          creditCard: {
            ...prev.data.creditCard,
            issuer,
          },
        },
        error: {
          ...prev.error,
          creditCard: {
            ...prev.error.creditCard,
            number: !isValid,
          },
        },
      }),
      () => {
        if (this.state.data.selectedPaymentMethod === PaymentMethod.CREDIT_CARD) {
          if (isValid && issuer && issuer !== "UNKNOWN") {
            this.loadCardInstallments(issuer);
          } else {
            this.resetInstallmentsState();
            this.loadPaymentSummaryInternal(PaymentMethod.CREDIT_CARD);
          }
        }
      }
    );
  }

  handleInstallmentSelection = (event: any) => {
    const value = Number(event.target.value);
    if (!Number.isFinite(value)) {
      return;
    }

    this.setState(
      (prev) => ({
        selectedInstallment: value,
        installmentsError: undefined,
        paymentSummaryError: undefined,
        paymentSummary: undefined,
        data: {
          ...prev.data,
          creditCard: {
            ...prev.data.creditCard,
            installments: value,
          },
        },
      }),
      () => {
        if (this.state.data.selectedPaymentMethod === PaymentMethod.CREDIT_CARD) {
          const issuer = this.state.data.creditCard.issuer?.toUpperCase();
          if (issuer && issuer !== "UNKNOWN") {
            this.loadPaymentSummaryInternal(
              PaymentMethod.CREDIT_CARD,
              issuer,
              value
            );
          }
        }
      }
    );
  };

  // ** PAY **
  async didClickPay() {
    const selectedMethod = this.state.data.selectedPaymentMethod;
    if (!selectedMethod) {
      return;
    }

    this.setState({
      isLoading: true,
    });

    if (this.state.data.purchaseData.length === 0) {
      this.setState((prev) => ({
        error: {
          ...prev.error,
          apiGeneral: false,
          apiValidation: true,
        },
        purchaseErrorMessage:
          "Adicione itens ao carrinho antes de finalizar o pagamento.",
        isLoading: false,
      }));
      return;
    }

    this.stopPaymentStatusPolling();
    this.setState({
      purchaseErrorMessage: undefined,
      paymentStatus: undefined,
      paymentStatusError: undefined,
      paymentStatusPolling: false,
    });

    const totalAmountCents = this.getCartTotalInCents();
    const currentInstallment =
      this.state.data.creditCard.installments ?? this.state.selectedInstallment;

    if (selectedMethod === PaymentMethod.CREDIT_CARD) {
      if (this.state.installmentsLoading || this.state.paymentSummaryLoading) {
        this.setState({ isLoading: false });
        return;
      }

      const installmentsData = this.state.installments;
      const summary = this.state.paymentSummary;
      const brand = this.state.data.creditCard.issuer?.toUpperCase() || "";

      if (!installmentsData) {
        this.setState({
          installmentsError:
            "Carregue as opções de parcelamento antes de confirmar o pagamento.",
          isLoading: false,
        });
        return;
      }

      if (!summary) {
        this.setState({
          paymentSummaryError:
            "Resumo de pagamento indisponível. Atualize as opções de parcelamento.",
          isLoading: false,
        });
        return;
      }

      if (summary.method !== PaymentMethod.CREDIT_CARD) {
        this.setState({
          paymentSummaryError:
            "Resumo de pagamento desatualizado para cartão. Recarregue as parcelas.",
          isLoading: false,
        });
        this.refreshPaymentData();
        return;
      }

      if (summary.brand && brand && summary.brand !== brand) {
        this.setState({
          paymentSummaryError:
            "Resumo de pagamento não corresponde à bandeira identificada. Recarregue as parcelas.",
          isLoading: false,
        });
        this.loadCardInstallments(brand);
        return;
      }

      const selectedOption = installmentsData.options.find(
        (option) => option.n === currentInstallment
      );

      if (!selectedOption) {
        this.setState({
          installmentsError: "Selecione uma opção de parcelamento válida.",
          isLoading: false,
        });
        return;
      }

      if (summary.amount_cents !== totalAmountCents) {
        this.setState({
          paymentSummaryError:
            "Resumo de pagamento desatualizado. Recarregue as parcelas antes de confirmar.",
          isLoading: false,
        });
        this.refreshPaymentData();
        return;
      }

      const summaryMax = summary.policy.max_allowed_installments_for_amount;
      const installmentsMax =
        installmentsData.max_allowed_installments_for_amount;
      if (
        summaryMax != null &&
        installmentsMax != null &&
        summaryMax !== installmentsMax
      ) {
        const message =
          "Os dados de parcelamento foram atualizados. Recarregue as opções antes de prosseguir.";
        this.setState({
          installmentsError: message,
          paymentSummaryError: message,
          isLoading: false,
        });
        this.loadCardInstallments(brand);
        return;
      }

      const effectiveMax = summaryMax ?? installmentsMax;
      if (effectiveMax != null && currentInstallment > effectiveMax) {
        this.setState({
          installmentsError: `Selecione até ${effectiveMax} parcelas para este valor.`,
          isLoading: false,
        });
        return;
      }

      const minInstallment = summary.policy.min_installment_value_cents;
      if (typeof minInstallment === "number") {
        let perInstallment = selectedOption.value_split_cents;
        if (perInstallment == null) {
          perInstallment = Math.floor(totalAmountCents / selectedOption.n);
        }
        if (perInstallment < minInstallment) {
          this.setState({
            installmentsError: `Cada parcela deve ser pelo menos ${this.formatCurrency(minInstallment)}.`,
            isLoading: false,
          });
          return;
        }
      }

      this.setState({
        installmentsError: undefined,
        paymentSummaryError: undefined,
      });
    } else {
      if (this.state.paymentSummaryLoading) {
        this.setState({ isLoading: false });
        return;
      }

      const summary = this.state.paymentSummary;
      if (!summary) {
        this.setState({
          paymentSummaryError:
            "Resumo de pagamento indisponível. Atualize o método selecionado.",
          isLoading: false,
        });
        this.refreshPaymentData();
        return;
      }

      if (summary.method !== selectedMethod) {
        this.setState({
          paymentSummaryError:
            "Resumo de pagamento desatualizado. Atualize o método selecionado.",
          isLoading: false,
        });
        this.refreshPaymentData();
        return;
      }

      if (summary.amount_cents !== totalAmountCents) {
        this.setState({
          paymentSummaryError:
            "Valor do resumo diferente do total do carrinho. Atualize o método antes de confirmar.",
          isLoading: false,
        });
        this.refreshPaymentData();
        return;
      }
    }

    this.trackAddPaymentInfo(selectedMethod);

    const profilePersisted =
      (await this.userInfoFormRef.current?.ensureProfilePersisted?.()) ?? true;
    if (!profilePersisted) {
      this.setState({ isLoading: false });
      return;
    }

    this.hasTrackedPaymentFailure = false;
    this.preparePurchasePixelPayload(selectedMethod, totalAmountCents);

    const fingerprintConfig = environment.vindi.isSandbox
      ? { env: "sandbox" }
      : { env: "production" };
    const fingerPrint = window.yapay.FingerPrint(fingerprintConfig);

    await this.yapayInitialized();

    console.log("Yapay FingerPrint initialized");

    fingerPrint.getFingerPrint();

    console.log("Yapay FingerPrint requested");

    await this.fingerPrintReady();
    const fingerprintValue = (
      document.querySelector('input[name="finger_print"]')! as any
    ).value;
    const token = localStorage.getItem("AccessToken")!;
    const addressId = this.state.selectedAddressId;
    if (addressId === undefined || addressId === null) {
      this.setState({
        purchaseErrorMessage: "Selecione um endereço válido para concluir a compra.",
        isLoading: false,
      });
      return;
    }

    const cartId = this.state.cartId;
    if (cartId === undefined || cartId === null) {
      this.setState({
        purchaseErrorMessage:
          "Não foi possível identificar o carrinho. Atualize a página e tente novamente.",
        isLoading: false,
      });
      return;
    }

    // Defense-in-depth on the money path: never POST an unrecognized brand.
    // The issuer is normalized to the brand enum (or "UNKNOWN") in
    // didIdentifyCreditCard; gate the purchase the same way installments/summary
    // are gated, so a non-enum value can't reach createPurchase.
    const ccIssuer = this.state.data.creditCard.issuer?.toUpperCase();
    if (
      this.state.data.selectedPaymentMethod === PaymentMethod.CREDIT_CARD &&
      (!ccIssuer || ccIssuer === "UNKNOWN")
    ) {
      this.setState({
        purchaseErrorMessage:
          "Bandeira de cartão não reconhecida. Verifique o número do cartão.",
        isLoading: false,
      });
      return;
    }

    const appliedCoupon = this.state.cartSummary.appliedCoupon?.code ?? undefined;
    this.controller.createPuchase(
      token,
      fingerprintValue,
      this.state.data,
      addressId,
      cartId,
      appliedCoupon
    );

  }

  async yapayInitialized(): Promise<void> {
    if (!(window.yapay && window.yapay.initialized)) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      return this.yapayInitialized();
    }
  }

  async fingerPrintReady(): Promise<void> {
    const element = document.querySelector('input[name="finger_print"]');
    if (!element) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      console.error("Yapay FingerPrint input not found");
      return this.fingerPrintReady();
    }
  }

  renderLoading() {
    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        marginTop={2}
      >
        <CircularProgress />
      </Box>
    );
  }

  renderPaymentMethodCard(method: { id: PaymentMethod; label: string; icon: React.ReactNode }, selected: boolean, readOnly = false, customMessage?: string) {
    return (
      <Card
        key={method.id}
        onClick={
          readOnly
            ? undefined
            : () => this.handlePaymentMethodChange({ target: { value: method.id } })
        }
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          p: 2,
          outline: selected ? "1px solid #1EA097" : "1px solid #ccc",
          borderRadius: 2,
          cursor: readOnly ? "default" : "pointer",
          opacity: readOnly ? 0.8 : 1,
          boxShadow: 'none'
        }}
      >
        {/* quadrado com símbolo */}
        <Box
          width={40}
          height={40}
          display="flex"
          alignItems="center"
          justifyContent="center"
          border="1px solid #ccc"
          borderRadius={1}
          mr={2}
          fontSize="1.5rem"
        >
          {method.icon}
        </Box>

        {/* nome da opção */}
        <Typography variant="body1">{customMessage ?? method.label}</Typography>
      </Card>
    );
  }

  renderReviewStep() {
    const purchaseItems = this.state.data.purchaseData;
    const isCartEmpty = purchaseItems.length === 0;
    const totalPrice = this.getCartTotalInCents();
    const totalPriceFormatted = this.formatCurrency(totalPrice);
    const selectedMethod = this.state.data.selectedPaymentMethod;
    const summary = this.state.paymentSummary;
    const installments = this.state.installments;
    const selectedInstallment =
      this.state.data.creditCard.installments ?? this.state.selectedInstallment;
    const hasPaymentError = Boolean(this.state.paymentSummaryError) || Boolean(this.state.installmentsError);
    const hasStatusError = Boolean(this.state.paymentStatusError);
    const isSummaryLoading =
      this.state.paymentSummaryLoading ||
      (selectedMethod === PaymentMethod.CREDIT_CARD && this.state.installmentsLoading);

    let finalAmountCents = totalPrice;
    if (selectedMethod === PaymentMethod.CREDIT_CARD) {
      const option = installments?.options.find(
        (item) => item.n === selectedInstallment
      );
      if (option?.value_total_cents != null) {
        finalAmountCents = option.value_total_cents;
      } else if (summary?.estimated_final_amount_cents != null) {
        finalAmountCents = summary.estimated_final_amount_cents;
      }
    } else if (selectedMethod && summary?.estimated_final_amount_cents != null) {
      finalAmountCents = summary.estimated_final_amount_cents;
    } else if (summary?.estimated_final_amount_cents != null) {
      finalAmountCents = summary.estimated_final_amount_cents;
    }

    const finalAmountFormatted = this.formatCurrency(finalAmountCents);

    return (
      <ReviewPanel
        purchaseItems={purchaseItems}
        isCartEmpty={isCartEmpty}
        totalPriceFormatted={totalPriceFormatted}
        selectedMethod={selectedMethod}
        methods={this.methods}
        renderMethodCard={(method, selected, readOnly, customMessage) =>
          this.renderPaymentMethodCard(
            method,
            selected,
            readOnly || isCartEmpty,
            customMessage
          )
        }
        selectedPaymentMethodLabel={this.getSelectedPaymentMethodLabel()}
        isSummaryLoading={isSummaryLoading}
        hasPaymentError={hasPaymentError}
        hasStatusError={hasStatusError}
        finalAmountFormatted={finalAmountFormatted}
        formatCurrency={(value) => this.formatCurrency(value)}
        paymentSummary={this.state.paymentSummary}
        paymentSummaryError={this.state.paymentSummaryError}
        paymentSummaryLoading={this.state.paymentSummaryLoading}
        installments={this.state.installments}
        installmentsError={this.state.installmentsError}
        selectedInstallment={selectedInstallment}
        couponDiscountCents={this.state.cartSummary.couponDiscountCents}
        subtotalCents={this.state.cartSummary.subtotalCents}
        progressiveDiscountCents={this.state.cartSummary.progressiveDiscountCents}
        appliedCoupon={this.state.cartSummary.appliedCoupon}
      />
    );
  }

  renderPaymentSummaryBanner() {
    const {
      paymentSummary,
      paymentSummaryError,
      paymentSummaryLoading,
    } = this.state;
    const method = this.state.data.selectedPaymentMethod;

    if (!method) {
      return null;
    }

    if (method === PaymentMethod.CREDIT_CARD) {
      return null;
    }

    if (paymentSummaryLoading) {
      return (
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          marginTop={2}
        >
          <CircularProgress size={20} />
          <Typography variant="body2">
            Carregando resumo do pagamento…
          </Typography>
        </Box>
      );
    }

    if (paymentSummaryError) {
      return (
        <Alert severity="error" sx={{ marginTop: 2 }}>
          {paymentSummaryError}
        </Alert>
      );
    }

    if (!paymentSummary) {
      return null;
    }

    if (method === PaymentMethod.BANK_SLIP) {
      const adjustment = paymentSummary.method_adjustment_cents || 0;
      if (adjustment > 0) {
        return (
          <Alert severity="info" sx={{ marginTop: 2 }}>
            {`+ ${this.formatCurrency(adjustment)} de taxa de boleto. Total estimado: ${this.formatCurrency(
              paymentSummary.estimated_final_amount_cents
            )}.`}
          </Alert>
        );
      }
    }

    return null;
  }

  renderUserInfoStep() {
    return (
      <UserInfoForm
        ref={this.userInfoFormRef}
        user={this.state.data.user}
        onUserChange={this.handleUserChange}
        loadUserProfile={this.fetchUserProfile}
        saveUserProfile={this.persistUserProfile}
        onProfileStateChange={this.handleProfileStateChange}
        getAccessToken={this.provideAccessToken}
        onLogout={this.requestLogout}
        onAddressSelected={this.handleAddressChange}
        onAddressStateChange={this.handleAddressStateChange}
      />
    );
  }

  renderPaymentInfoTable() {
    const {
      user,
    } = this.state.data;

    const isCartEmpty = this.state.data.purchaseData.length === 0;

    const isIdentificationFilled = Boolean(user.identification?.trim?.() || user.identification.length);
    const isBirthDateFilled = Boolean(
      (user.birthDate?.day && user.birthDate.day.length > 0) &&
      (user.birthDate?.month && user.birthDate.month.length > 0) &&
      (user.birthDate?.year && user.birthDate.year.length > 0)
    );
    const isCellphoneFilled = Boolean(user.cellphone && user.cellphone.length > 0);
    const isUserInfoComplete = isIdentificationFilled && isBirthDateFilled && isCellphoneFilled;

    return (
      <Container maxWidth="xl"
        style={{
          padding: "60px 20px",
        }}
      >
        <Box mb={4}>
          <Stepper activeStep={this.state.data.currentStep} alternativeLabel>
            <Step><StepLabel>Dados do Comprador</StepLabel></Step>
            <Step><StepLabel>Pagamento</StepLabel></Step>
          </Stepper>
        </Box>

        {isCartEmpty && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Seu carrinho está vazio. Adicione itens para concluir a compra.
          </Alert>
        )}

        <Box
          display="flex"
          gap={3}
          alignItems="flex-start"
          sx={{
            flexDirection: { xs: 'column-reverse', lg: 'row' }
          }}
        >
          <Box flex={2} bgcolor={"white"} borderRadius={2} width={'100%'} boxSizing={'border-box'} padding={0}>
            {this.state.data.currentStep === PaymentStep.USER_INFO && (
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  p: { xs: 2, sm: 3.5 },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                }}
              >
                {this.renderUserInfoStep()}
                <Divider />
                <Box textAlign="right" display={'flex'} gap={2} justifyContent={'flex-end'} flexWrap={'wrap'}>
                  <Link to="/checkout/review" style={{ textDecoration: "none" }}>
                    <Button
                      color="secondary"
                      startIcon={<KeyboardArrowLeft />}
                      variant="outlined"
                    >
                      Voltar ao carrinho
                    </Button>
                  </Link>
                  <Button
                    variant="contained"
                    disabled={
                      this.state.error.user.identification ||
                      this.state.error.user.birthDate ||
                      this.state.error.user.cellphone ||
                      this.state.error.user.cnpj ||
                      this.state.addressesBusy ||
                      this.state.userProfileBusy ||
                      !this.state.selectedAddressId ||
                      !isUserInfoComplete ||
                      isCartEmpty
                    }
                    onClick={() => {
                      void this.handleProceedToPayment();
                    }}
                  >
                    Continuar para Pagamento
                  </Button>
                </Box>
              </Paper>
            )}

            {this.state.data.currentStep === PaymentStep.PAYMENT && (
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  p: { xs: 2, sm: 3.5 },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                }}
              >
                <PaymentMethodForm
                  methods={this.methods}
                  selectedMethod={this.state.data.selectedPaymentMethod}
                  onSelectMethod={(method) =>
                    this.handlePaymentMethodChange({ target: { value: method } })
                  }
                  summaryBanner={this.renderPaymentSummaryBanner()}
                  creditCard={this.state.data.creditCard}
                  creditCardErrors={this.state.error.creditCard}
                  focusedField={this.state.data.focusedCCField}
                  onCCNameChange={this.handleCCNameChange}
                  onCCNumberChange={this.handleCCNumberChange}
                  onCCExpiryChange={this.handleCCExpiryChange}
                  onCCCVCChange={this.handleCCCVCChange}
                  onCCFocusChange={(field) => this.didChangeCCFocus(field)}
                  onIdentifyCreditCard={(data, isValid) =>
                    this.didIdentifyCreditCard(data, isValid)
                  }
                  installments={this.state.installments}
                  installmentsLoading={this.state.installmentsLoading}
                  installmentsError={this.state.installmentsError}
                  selectedInstallment={this.state.selectedInstallment}
                  onInstallmentSelection={this.handleInstallmentSelection}
                  paymentSummary={this.state.paymentSummary}
                  paymentSummaryError={this.state.paymentSummaryError}
                  paymentSummaryLoading={this.state.paymentSummaryLoading}
                  cardBrandRequiredMessage={this.cardBrandRequiredMessage}
                  formatCurrency={(value) => this.formatCurrency(value)}
                />
                <Divider />
                <Box textAlign="right" display={'flex'} gap={2} justifyContent={'flex-end'} flexWrap={'wrap'}>
                  <Button
                    startIcon={<KeyboardArrowLeft />}
                    variant="outlined"
                    color="secondary"
                    onClick={() => this.didClickStep(PaymentStep.USER_INFO)}
                  >
                    Alterar dados
                  </Button>

                  <Button
                    sx={{
                      borderRadius: 999,
                      px: 3,
                      py: 1,
                    }}
                    variant="contained"
                    disabled={
                      isCartEmpty ||
                      !this.state.data.selectedPaymentMethod ||
                      this.state.paymentSummaryLoading ||
                      !!this.state.paymentSummaryError ||
                      (
                        this.state.data.selectedPaymentMethod === PaymentMethod.CREDIT_CARD &&
                        (
                          !this.state.data.creditCard.number ||
                          !this.state.data.creditCard.expiry ||
                          !this.state.data.creditCard.cvc ||
                          !this.state.data.creditCard.name ||
                          this.state.error.creditCard.number ||
                          this.state.error.creditCard.expiry ||
                          this.state.error.creditCard.cvc ||
                          this.state.error.creditCard.name ||
                          this.state.installmentsLoading ||
                          !!this.state.installmentsError
                        )
                      )
                    }
                    onClick={() => this.didClickPay()}
                  >
                    Confirmar Pagamento
                  </Button>
                </Box>
              </Paper>
            )}

            {this.state.error.apiValidation && (
              <Box m={2} alignSelf="flex-end">
                <Typography color="error">
                  {this.state.purchaseErrorMessage ??
                    "Erro ao validar os dados enviados. Faça uma revisão dos dados e envie novamente."}
                </Typography>
              </Box>
            )}
          </Box>

          <Box flex={1} bgcolor={"white"} borderRadius={2} width={'100%'}>
            {this.renderReviewStep()}
          </Box>
        </Box>
      </Container>
    );
  }


  renderPaymentSuccess() {
    const paymentResponse = this.state.data.paymentResponse!;

    if (paymentResponse.paymentQrCode) {
      return (
        <PaymentResult
          variant="pix"
          paymentResponse={paymentResponse}
          paymentStatus={this.state.paymentStatus}
          paymentStatusPolling={this.state.paymentStatusPolling}
          paymentStatusError={this.state.paymentStatusError}
          onBack={() => {
            this.props.navigationHooks("/");
          }}
        />
      );
    }

    if (paymentResponse.paymentUrl) {
      return (
        <PaymentResult
          variant="bank-slip"
          paymentResponse={paymentResponse}
          onBack={() => {
            this.props.navigationHooks("/");
          }}
        />
      );
    }

    return (
      <PaymentResult
        variant="credit-card"
        paymentStatus={this.state.paymentStatus}
        paymentStatusPolling={this.state.paymentStatusPolling}
        paymentStatusError={this.state.paymentStatusError}
        onBack={() => {
          this.props.navigationHooks("/");
        }}
      />
    );
  }

  selectRender() {
    if (this.state.isLoading) {
      return this.renderLoading();
    }
    if (this.state.error.apiGeneral) {
      return (
        <PaymentResult
          variant="error"
          message={this.state.purchaseErrorMessage}
          onBack={() => {
            this.props.navigationHooks(-1);
          }}
        />
      );
    }
    if (this.state.data.paymentResponse) {
      return this.renderPaymentSuccess();
    }

    return this.renderPaymentInfoTable();
  }

  render() {
    return (
      <Layout>
        <form data-yapay="payment-form"></form>
        {this.selectRender()}
      </Layout>
    );
  }
}

export default withNavigationHooks(ManagedInstruments);
